import { useMemo } from 'react'
import { MapPin } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFacilityLocations } from '@/hooks/useFacility'
import { formatPlace, indexRooms, roomOptionGroups } from '@/lib/facilityLocation'

const NONE = '__none__'

/**
 * Pick the room a piece of equipment stands in (z8nrz7fw25). The facility,
 * building and floor come with the room, so they are configured once on the
 * Facility set-up and never typed again here.
 *
 * Rooms are grouped by floor address; the chosen room's full address is shown
 * under the picker so the person sees exactly where the unit will read as.
 */
export function RoomPicker({
  value,
  onChange,
  disabled,
}: {
  value: string | null
  onChange: (roomId: string | null) => void
  disabled?: boolean
}) {
  const locations = useFacilityLocations()
  const groups = useMemo(() => (locations.data ? roomOptionGroups(locations.data) : []), [locations.data])
  const place = useMemo(() => {
    if (!value || !locations.data) return null
    const at = indexRooms(locations.data).get(value)
    return at ? formatPlace(at) : null
  }, [value, locations.data])

  const noRooms = !locations.isLoading && groups.length === 0

  return (
    <div className='space-y-1'>
      <Select
        value={value ?? NONE}
        onValueChange={(v) => onChange(v === NONE ? null : v)}
        disabled={disabled || locations.isLoading || noRooms}
      >
        <SelectTrigger aria-label='Room'>
          <SelectValue placeholder={locations.isLoading ? 'Loading rooms…' : 'Choose a room…'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Not in a room yet</SelectItem>
          {groups.map((g) => (
            <SelectGroup key={g.heading}>
              <SelectSeparator />
              <SelectLabel className='text-xs text-muted-foreground'>{g.heading}</SelectLabel>
              {g.rooms.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.label}
                  {r.name && <span className='ml-1.5 text-muted-foreground'>· {r.name}</span>}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      {place && (
        <p className='flex items-center gap-1 text-xs text-muted-foreground'>
          <MapPin className='h-3 w-3 shrink-0' />
          {place}
        </p>
      )}
      {noRooms && (
        <p className='text-xs text-amber-700'>
          No rooms yet. Add rooms on the Rooms page and place them on a floor in the facility set-up.
        </p>
      )}
      {locations.isError && <p className='text-xs text-destructive'>Rooms could not be loaded.</p>}
    </div>
  )
}
